"use client";

import {
  getCurrencyBasedOnLanguage,
  getPromotionBadgeName,
  hasArrayElements,
  toPrecise,
} from "@/apiUtils/commonUtils";
import { giftTypes, promotionType, SaleorAttributes } from "@/apiUtils/constants";
import { buildProductPriceInfo, getDisplayDiscountPercent, getPriceForBuyXgetY, getSpecsForProductType } from "@/apiUtils/productUtils";
import ClickNCollectIcon from "@/assets/icons/clickNcollect.svg?react";
import NoExchangeIcon from "@/assets/icons/no-exchange.svg?react";
import { CheckoutLines } from "@/common/lib/types";
import { cn } from "@/lib/tailwind";
import { getAttributeKeyValuePairs } from "@/utils/cart.utils";
import {
  allowsSameCatalogueLineQuantityChange,
  getLensAddOns,
  getSameCatalogueDiscountDisplay,
  isFixedRewardType,
  isPartialDiscountSameCatalogueLine,
  resolveCheckoutLineProductUrl,
  shouldShowLineAsFreeGift,
} from "@/utils/cartGiftUtils";
import { getLangFromLocale } from "@/utils/common";
import {
  isAnyPromotionLimitReached,
  isAnyPerCodeLimitReached,
  isAnyPromotionBlockedByCoupon,
  isPromotionBlockedByCoupon,
  isPromotionLimitReached,
  getLinePreCouponUnitPrice,
  getLineProductCardUnitPrice,
  getLineRetailUnitPrice,
  isPromotionalCartPrice,
  resolvePrimaryAppliedPromotion,
  isPaidCheckoutMerchandiseLine,
  shouldUseUndiscountedPromotionPrice,
} from "@/utils/promotionUtils";
import { getLineDisplayPrices } from "@/utils/orderSummaryPrices";
import { useParams } from "next/navigation";
import { useMemo } from "react";
import CartImage from "./CartImage";
import CartInfo from "./CartInfo";
import CartPricing from "./CartPricing";
import CNCBadge from "@/components/pdp/CNCBadge";
import Divider from "@/components/ui/divider/Divider";
import CartItemFeatures from "./CartItemFeatures";
import RenderPrice from "@/components/productPrice/RenderPrice";
import { ProductAttributeOverride } from "@/common/lib/types";

interface IProps {
  cartId: string;
  cartItem: CheckoutLines;
  staticText?: Record<string, string> | undefined;
  enableClickAndCollect?: boolean;
  preview?: boolean;
  /** When true, always hides quantity controls and the remove icon regardless of other flags. */
  hideControls?: boolean;
  /** Renders auto-added / single-choice free gift like a normal line: specs + “Free” instead of price. */
  displayAsCartFreeGift?: boolean;
  showExclusiveBrandCouponMessage?: boolean;
  onChangeQuantity?: (newQuantity: number, cartItem: CheckoutLines) => Promise<void>;
  onDeleteItem?: (cartItem: CheckoutLines) => Promise<void>;
  productAttributeOverrides?: ProductAttributeOverride[];
  voucherCode?: string | null;
  voucherDiscountAmount?: number;
  checkoutLines?: CheckoutLines[];
  undiscountedSubtotalGross?: number | null;
  paidSubtotalGross?: number | null;
  checkoutMerchandiseLineCount?: number;
}

const slugs = [
  SaleorAttributes.PRODUCT_IMAGE,
  SaleorAttributes.CL_PRODUCT_IMAGE,
  SaleorAttributes.TITLE,
  SaleorAttributes.IS_RETURNABLE,
  SaleorAttributes.CLIPON_FRAMECOLOR,
  SaleorAttributes.CLIPON_STYLE,
  // SaleorAttributes.FRAME_COLOR,
  // SaleorAttributes.LENS_COLOR,
  // SaleorAttributes.FRAME_FIT,
  // SaleorAttributes.GENDER,
  SaleorAttributes.MODEL,
  // SaleorAttributes.CL_PER_PACK_SIZE,
  // SaleorAttributes.CL_POWER,
];

const CartItemCard = ({
  cartId,
  cartItem,
  staticText,
  preview = false,
  hideControls = false,
  displayAsCartFreeGift = false,
  showExclusiveBrandCouponMessage = true,
  enableClickAndCollect,
  onChangeQuantity,
  onDeleteItem,
  productAttributeOverrides,
  voucherCode,
  voucherDiscountAmount = 0,
  checkoutLines,
  undiscountedSubtotalGross,
  paidSubtotalGross,
  checkoutMerchandiseLineCount,
}: IProps) => {
  const params = useParams();
  const lang = getLangFromLocale(params.locale as string);
  const { variant, appliedPromotions, totalPrice, unitPrice, undiscountedUnitPrice } = cartItem || {};
  const {
    id: variantId,
    product,
    pricing,
    landingPageBaseUrl,
    availablePromotions,
  } = variant || {};

  const [availablePromotion] = availablePromotions || [];
  /** Order-success snapshot lines only — live checkout lines may also carry undiscountedUnitPrice after coupons. */
  const isOrderLine = preview && Boolean(undiscountedUnitPrice?.gross);
  const isSameCatalogueFreeLine = allowsSameCatalogueLineQuantityChange(cartItem);
  const chargedLineTotal =
    totalPrice?.gross?.amount ?? (unitPrice?.gross?.amount ?? 0) * (cartItem?.quantity || 1);
  const hasChargedAmount = chargedLineTotal > 0.01;
  const lineShowsAsFreeGift =
    !hasChargedAmount &&
    (displayAsCartFreeGift || shouldShowLineAsFreeGift(cartItem)) &&
    !isPaidCheckoutMerchandiseLine(cartItem);

  const appliedPromotion = useMemo(
    () => resolvePrimaryAppliedPromotion(appliedPromotions),
    [appliedPromotions],
  );
  const cataloguePromotion = appliedPromotion ?? availablePromotion;

  const promotionForBadge = useMemo(() => {
    if (isOrderLine) {
      const orderBxgyTrigger = (appliedPromotions || []).find(
        (promo) =>
          promo.promotion?.type === promotionType.BUYXGETY &&
          promo.giftType !== giftTypes.FREE_GIFT &&
          (promo.promotion?.showBadgeInStorefront ?? true),
      );
      if (orderBxgyTrigger) {
        return orderBxgyTrigger;
      }

      const orderAppliedPromo = (appliedPromotions || []).find(
        (promo) =>
          promo.giftType !== giftTypes.FREE_GIFT &&
          (promo.promotion?.showBadgeInStorefront ?? true) &&
          promo.promotion?.type !== "ORDER",
      );
      if (orderAppliedPromo) {
        return orderAppliedPromo;
      }
    }

    if (appliedPromotion && !isPromotionLimitReached(appliedPromotion) && !isPromotionBlockedByCoupon(appliedPromotion, !!voucherCode)) {
      return appliedPromotion;
    }

    return (availablePromotions || []).find(
      (promo) =>
        !isPromotionLimitReached(promo) &&
        !isPromotionBlockedByCoupon(promo, !!voucherCode) &&
        (!promo?.ineligibilityReason || promo.ineligibilityReason === "ELIGIBLE") &&
        promo?.promotion?.showBadgeInStorefront !== false,
    );
  }, [appliedPromotion, availablePromotions, voucherCode, isOrderLine]);

  const itemInfo = useMemo(() => {
    const extractedData = getAttributeKeyValuePairs(variant?.attributes, lang, slugs);
    const {
      [SaleorAttributes.PRODUCT_IMAGE]: thumbnail,
      [SaleorAttributes.CL_PRODUCT_IMAGE]: clThumbnail,
      [SaleorAttributes.TITLE]: variantName,
      [SaleorAttributes.IS_RETURNABLE]: isReturnable,
      [SaleorAttributes.CLIPON_FRAMECOLOR]: clipon_framecolor,
      [SaleorAttributes.CLIPON_STYLE]: clipon_style,
      // [SaleorAttributes.FRAME_COLOR]: frameColor,
      // [SaleorAttributes.LENS_COLOR]: lensColor,
      // [SaleorAttributes.FRAME_FIT]: frameFit,
      // [SaleorAttributes.GENDER]: gender,
      [SaleorAttributes.MODEL]: model,
      // [SaleorAttributes.CL_PER_PACK_SIZE]: lensesPerPack,
      // [SaleorAttributes.CL_POWER]: lensesPower,
    } = extractedData;

    return {
      thumbnail,
      clThumbnail,
      variantName,
      isReturnable,
      clipon_framecolor,
      clipon_style,
      // frameColor,
      // lensColor,
      // frameFit,
      // gender,
      model,
      // lensesPerPack,
      // lensesPower,
    };
  }, [lang, variantId, variant?.attributes]);

  const itemDetails = useMemo(
    () =>
      getSpecsForProductType(
        product?.productType?.slug ?? "",
        variant?.attributes,
        product?.attributes ?? [],
        staticText,
        lang,
        product?.id,
        productAttributeOverrides,
      ),
    [lang, variant?.attributes, product?.id, productAttributeOverrides],
  );

  const productUrl = useMemo(() => resolveCheckoutLineProductUrl(variant), [variant]);

  const currency: string = useMemo(() => {
    const currencyCode =
      (undiscountedUnitPrice?.gross
        ? unitPrice?.gross?.currency
        : pricing?.price?.gross?.currency) || "";
    return (lang === "en" ? currencyCode : getCurrencyBasedOnLanguage(currencyCode)) || "";
  }, [lang, pricing?.price?.gross?.currency, unitPrice?.gross?.currency, undiscountedUnitPrice?.gross]);

  // Order-success / preview: only treat the line as discounted if paid < undiscounted.
  const orderHadDiscount = useMemo(() => {
    if (!isOrderLine) return false;
    const paid = unitPrice?.gross?.amount ?? 0;
    const undiscounted = undiscountedUnitPrice?.gross?.amount ?? paid;
    return paid < undiscounted;
  }, [isOrderLine, unitPrice, undiscountedUnitPrice]);

  const isOutOfStock = useMemo(() => {
    return !(variant?.quantityAvailable > 0);
  }, [variant]);

  const lensAddOns = useMemo(() => getLensAddOns(cartItem), [cartItem]);

  const selectedLenPrice = useMemo(
    () =>
      lensAddOns.reduce((acc, item) => {
        return acc + (item?.totalPrice?.gross?.amount ?? 0);
      }, 0),
    [lensAddOns],
  );

  const undiscountedSelectedLenPrice = useMemo(
    () =>
      lensAddOns.reduce((acc, item) => {
        return acc + (item?.variant?.pricing.priceUndiscounted.gross?.amount ?? 0);
      }, 0),
    [lensAddOns],
  );

  const { price, costPrice, priceUndiscounted } = pricing || {};

  const saleorLinePrices = getLineDisplayPrices(cartItem);

  const salePrice = useMemo(() => {
    if (saleorLinePrices.hasSaleorDisplayPrice) {
      return saleorLinePrices.unitAmount;
    }
    if (isOrderLine) {
      return unitPrice?.gross?.amount ?? 0;
    }
    let resolvedPrice = price?.gross?.amount ?? 0;
    if (appliedPromotion?.promotion?.type === promotionType.BUYXGETY) {
      resolvedPrice =
        unitPrice?.gross?.amount ??
        (totalPrice?.gross?.amount != null && cartItem?.quantity
          ? totalPrice.gross.amount / cartItem.quantity
          : undefined) ??
        price?.gross?.amount ??
        0;
    }

    if (voucherCode && !lineShowsAsFreeGift) {
      resolvedPrice = getLinePreCouponUnitPrice(cartItem, voucherDiscountAmount, checkoutLines, {
        undiscountedSubtotalGross,
        checkoutMerchandiseLineCount,
      });
    }

    return resolvedPrice;
  }, [
    isOrderLine,
    saleorLinePrices.hasSaleorDisplayPrice,
    saleorLinePrices.unitAmount,
    unitPrice?.gross?.amount,
    price?.gross?.amount,
    appliedPromotion?.promotion?.type,
    totalPrice?.gross?.amount,
    cartItem,
    voucherCode,
    voucherDiscountAmount,
    checkoutLines,
    lineShowsAsFreeGift,
    undiscountedSubtotalGross,
    checkoutMerchandiseLineCount,
  ]);

  const listPrice: number = isOrderLine
    ? (undiscountedUnitPrice?.gross?.amount ?? unitPrice?.gross?.amount ?? 0)
    : (costPrice?.gross?.amount ?? priceUndiscounted?.gross?.amount ?? price?.gross?.amount ?? 0);
  const isFlatOff = isFixedRewardType(cataloguePromotion?.rewardValueType);

  const netTotal: number = salePrice * cartItem?.quantity;
  const itemTotal: number = selectedLenPrice + netTotal;

  const retailUnitPrice = getLineRetailUnitPrice(cartItem, {
    undiscountedSubtotalGross,
    checkoutMerchandiseLineCount,
    paidSubtotalGross,
  });

  const useUndiscountedPromotionPrice = shouldUseUndiscountedPromotionPrice(
    appliedPromotions,
    availablePromotions,
    !!voucherCode,
  );

  const isCheckoutPromotionLimitReached = useMemo(
    () =>
      isAnyPromotionLimitReached(appliedPromotions, availablePromotions) ||
      isAnyPromotionBlockedByCoupon(appliedPromotions, availablePromotions, !!voucherCode) ||
      (checkoutLines ?? []).some((line) =>
        isAnyPromotionLimitReached(line.appliedPromotions, line.variant?.availablePromotions) ||
        isAnyPromotionBlockedByCoupon(
          line.appliedPromotions,
          line.variant?.availablePromotions,
          !!voucherCode,
        ),
      ),
    [appliedPromotions, availablePromotions, checkoutLines, voucherCode],
  );

  // Magrabi parity: coupon conflict / promo limits revoke Free so banners stay sane (MLC-1830).
  const showAsFreeGift =
    lineShowsAsFreeGift &&
    !isCheckoutPromotionLimitReached &&
    !useUndiscountedPromotionPrice;

  const hardPromoLimitReached =
    isAnyPromotionLimitReached(appliedPromotions, availablePromotions) ||
    (checkoutLines ?? []).some((line) =>
      isAnyPromotionLimitReached(line.appliedPromotions, line.variant?.availablePromotions),
    );

  const { displayIsFlatOff } = useMemo(() => {
    if (showAsFreeGift && !isSameCatalogueFreeLine) {
      return { isDiscounted: false, discount: 0, displaySalePrice: 0, displayIsFlatOff: false };
    }
    if (isSameCatalogueFreeLine) {
      return { isDiscounted: false, discount: 0, displaySalePrice: 0, displayIsFlatOff: false };
    }
    if (isOrderLine) {
      const paid = unitPrice?.gross?.amount ?? 0;
      const undiscounted = undiscountedUnitPrice?.gross?.amount ?? paid;
      const hasDiscount = paid < undiscounted;
      const catalogPercent = getDisplayDiscountPercent(cartItem?.variant?.pricing, appliedPromotion);
      return {
        isDiscounted: hasDiscount,
        discount: hasDiscount ? (isFlatOff ? toPrecise(undiscounted - paid) : catalogPercent) : 0,
        displaySalePrice: paid,
        displayIsFlatOff: isFlatOff,
      };
    }
    if (appliedPromotion?.promotion?.type === promotionType.BUYXGETY) {
      const lineMerchandiseTotal =
        totalPrice?.gross?.amount ?? salePrice * (cartItem?.quantity || 1);
      const { percent, hasDiscount, discountedPrice } = getPriceForBuyXgetY(
        listPrice,
        lineMerchandiseTotal,
        cartItem?.quantity || 1,
      );
      const configuredDiscount = getSameCatalogueDiscountDisplay(
        appliedPromotion,
        listPrice,
        discountedPrice,
      );
      const useConfiguredDiscount =
        appliedPromotion?.giftType === giftTypes.SAME_CATALOGUE && configuredDiscount.discount > 0;

      return {
        isDiscounted: hasDiscount,
        discount: useConfiguredDiscount
          ? configuredDiscount.discount
          : isFlatOff
            ? toPrecise(listPrice - discountedPrice)
            : Math.abs(percent),
        displaySalePrice: discountedPrice,
        displayIsFlatOff: useConfiguredDiscount ? configuredDiscount.isFlatOff : isFlatOff,
      };
    }
    const unitSalePrice = price?.gross?.amount ?? 0;
    const cartPriceInfo = buildProductPriceInfo(
      listPrice,
      unitSalePrice,
      cataloguePromotion,
      cartItem?.variant?.pricing,
    );
    return {
      isDiscounted: cartPriceInfo.hasDiscount,
      discount: cartPriceInfo.discountValue
        ? toPrecise(Math.abs(Number(cartPriceInfo.discountValue)))
        : (cartPriceInfo.discountPercent ?? 0),
      displaySalePrice: cartPriceInfo.price,
      displayIsFlatOff: Boolean(cartPriceInfo.discountValue),
    };
  }, [
    showAsFreeGift,
    isSameCatalogueFreeLine,
    isOrderLine,
    unitPrice?.gross?.amount,
    undiscountedUnitPrice?.gross?.amount,
    appliedPromotion,
    appliedPromotion?.promotion?.type,
    appliedPromotion?.giftType,
    isFlatOff,
    listPrice,
    price?.gross?.amount,
    totalPrice?.gross?.amount,
    salePrice,
    cartItem?.quantity,
    voucherCode,
    cartItem,
    undiscountedSubtotalGross,
    checkoutMerchandiseLineCount,
    paidSubtotalGross,
  ]);

  const productCardUnitPrice = useMemo(() => {
    if (showAsFreeGift) {
      return 0;
    }
    if (hardPromoLimitReached && !voucherCode) {
      return retailUnitPrice > 0 ? retailUnitPrice : salePrice;
    }
    if (useUndiscountedPromotionPrice && !voucherCode) return retailUnitPrice;

    const paidLineGross = totalPrice?.gross?.amount ?? 0;
    const quantity = cartItem?.quantity || 1;
    const paidUnit = paidLineGross > 0.01 && quantity > 0 ? paidLineGross / quantity : 0;

    // Order success (Magrabi parity): line total already includes catalog + coupon.
    if (
      isOrderLine &&
      paidUnit > 0.01 &&
      retailUnitPrice > 0.01 &&
      paidUnit < retailUnitPrice - 0.01
    ) {
      const couponStillOnOrderOnly =
        Boolean(voucherCode) &&
        voucherDiscountAmount > 0.01 &&
        paidUnit + voucherDiscountAmount >= retailUnitPrice - 0.02;
      if (!couponStillOnOrderOnly) {
        return paidUnit;
      }
    }

    const cardPrice = getLineProductCardUnitPrice(cartItem, {
      voucherCode,
      undiscountedSubtotalGross,
      checkoutMerchandiseLineCount,
      paidSubtotalGross,
      voucherDiscountAmount,
      checkoutLines,
      appliedPromotions,
      availablePromotions,
    });

    if (voucherCode) {
      // Payable line gross already carries catalogue + subtotal-scope voucher, while cardPrice
      // only reflects the catalogue promotion, so an order-level coupon would be dropped here.
      if (paidUnit > 0.01 && paidUnit < retailUnitPrice - 0.01) {
        return paidUnit;
      }
      if (cardPrice > 0.01 && cardPrice < retailUnitPrice - 0.01) {
        return cardPrice;
      }
      // Logged-in: coupon often sits on checkout discount while line gross stays at list.
      if (voucherDiscountAmount > 0.01 && retailUnitPrice > 0.01) {
        if (paidUnit <= 0.01 || Math.abs(paidUnit - retailUnitPrice) <= 0.02) {
          return Math.max(0, retailUnitPrice - voucherDiscountAmount);
        }
      }
      // Order success: unitPrice is often catalog-only; voucher discount sits on the order.
      if (
        isOrderLine &&
        paidUnit > 0.01 &&
        retailUnitPrice > 0.01 &&
        voucherDiscountAmount > 0.01 &&
        paidUnit < retailUnitPrice - 0.01 &&
        paidUnit + voucherDiscountAmount >= retailUnitPrice - 0.02
      ) {
        return Math.max(0, paidUnit - voucherDiscountAmount);
      }
      if (paidUnit > 0.01 && paidUnit < retailUnitPrice - 0.01) {
        return paidUnit;
      }
      if (cardPrice > 0.01) {
        return cardPrice;
      }
      return retailUnitPrice > 0.01 ? retailUnitPrice : salePrice;
    }

    if (paidUnit > 0.01) {
      return paidUnit;
    }

    if (cardPrice > 0.01 && cardPrice < retailUnitPrice - 0.01) {
      return cardPrice;
    }
    // VAT-inclusive retail (567) matches PDP / dashboard — not checkout net (540).
    if (retailUnitPrice > 0.01) {
      return retailUnitPrice;
    }
    return salePrice;
  }, [
    showAsFreeGift,
    hardPromoLimitReached,
    useUndiscountedPromotionPrice,
    retailUnitPrice,
    voucherCode,
    cartItem,
    undiscountedSubtotalGross,
    checkoutMerchandiseLineCount,
    paidSubtotalGross,
    voucherDiscountAmount,
    checkoutLines,
    appliedPromotions,
    availablePromotions,
    salePrice,
    isOrderLine,
    totalPrice?.gross?.amount,
  ]);

  // Strikethrough (grey) = Magrabi retail list — VAT-inclusive, matches order subtotal.
  const cardListPrice = retailUnitPrice > 0.01 ? retailUnitPrice : listPrice;

  // Red sale = productCardUnitPrice (payable unit). Strike when list > sale, same as Magrabi.
  const showCardPromotionalPrice = useMemo(() => {
    if (showAsFreeGift) {
      return false;
    }
    if (useUndiscountedPromotionPrice && !voucherCode) {
      return false;
    }
    if (isCheckoutPromotionLimitReached && !voucherCode) {
      return false;
    }
    if (isOrderLine && !orderHadDiscount) {
      return false;
    }
    return isPromotionalCartPrice(cardListPrice, productCardUnitPrice);
  }, [
    showAsFreeGift,
    useUndiscountedPromotionPrice,
    voucherCode,
    isCheckoutPromotionLimitReached,
    isOrderLine,
    orderHadDiscount,
    cardListPrice,
    productCardUnitPrice,
  ]);

  const cardDiscount = useMemo(() => {
    if (!showCardPromotionalPrice) return null;
    if (isFlatOff || displayIsFlatOff) return cartItem?.variant?.pricing?.discount?.gross?.amount ?? null;
    return getDisplayDiscountPercent(cartItem?.variant?.pricing, appliedPromotion);
  }, [
    showCardPromotionalPrice,
    isFlatOff,
    displayIsFlatOff,
    cartItem?.variant?.pricing,
    appliedPromotion,
  ]);

  const effectiveSalePrice = productCardUnitPrice;
  const effectiveItemTotal =
    productCardUnitPrice * (cartItem?.quantity || 1) + selectedLenPrice;
  const formattedEffectiveItemTotal = useMemo(
    () => (Number.isInteger(effectiveItemTotal) ? `${effectiveItemTotal}` : effectiveItemTotal?.toFixed(2)),
    [effectiveItemTotal],
  );

  const isGiftPromotedProduct = useMemo(() => {
    if ((cartItem?.parent?.id ?? "") === "" && hasChargedAmount && !displayAsCartFreeGift) {
      return false;
    }

    if (isPaidCheckoutMerchandiseLine(cartItem)) {
      return false;
    }

    if (isPartialDiscountSameCatalogueLine(cartItem)) {
      return false;
    }

    const giftType = appliedPromotion?.giftType;
    const promoType = appliedPromotion?.promotion?.type;

    const isGiftLine =
      (promoType === promotionType.BUYXGETY && giftType === giftTypes.FREE_GIFT) ||
      !!cartItem.isGift ||
      !!cartItem.isBuyxGetyGift;

    if (
      isGiftLine &&
      isPaidCheckoutMerchandiseLine(cartItem) &&
      (isCheckoutPromotionLimitReached ||
        isAnyPromotionLimitReached(appliedPromotions, availablePromotions) ||
        isAnyPerCodeLimitReached(appliedPromotions, availablePromotions))
    ) {
      return false;
    }

    if (promoType === promotionType.BUYXGETY && giftType === giftTypes.FREE_GIFT) {
      return !isPaidCheckoutMerchandiseLine(cartItem);
    }

    if (cartItem.isGift) {
      return true;
    }

    if (cartItem.isBuyxGetyGift) {
      return !isPaidCheckoutMerchandiseLine(cartItem);
    }

    return isGiftLine;
  }, [
    appliedPromotion,
    cartItem,
    hasChargedAmount,
    displayAsCartFreeGift,
    isCheckoutPromotionLimitReached,
    appliedPromotions,
    availablePromotions,
  ]);

  const promotionBadgeName = useMemo(() => {
    if (!promotionForBadge) return "";
    if (!isOrderLine && isPromotionLimitReached(promotionForBadge)) return "";
    return getPromotionBadgeName(
      {
        ...promotionForBadge?.promotion,
        translation: promotionForBadge?.translation ?? promotionForBadge?.promotion?.translation,
      },
      lang,
    );
  }, [promotionForBadge, lang, isOrderLine]);

  // Hide unpaid auto-gift duplicates when a hard promo limit revoked Free (Magrabi parity).
  // Never hide Saleor-marked non-gift buy-X parents that only carry FREE_GIFT reward metadata.
  const isExplicitNonGiftParent =
    cartItem?.isGift === false &&
    cartItem?.isBuyxGetyGift === false &&
    !cartItem?.buyGetGiftFlag;
  const hideUnpaidGiftDuplicate =
    isGiftPromotedProduct &&
    !showAsFreeGift &&
    !isPaidCheckoutMerchandiseLine(cartItem) &&
    !hasChargedAmount &&
    hardPromoLimitReached &&
    !isExplicitNonGiftParent;
  if (hideUnpaidGiftDuplicate) {
    return null;
  }

  const hasCost = Boolean(effectiveItemTotal > 0.01);
  const displayHasCost = showAsFreeGift ? false : hasCost;
  const isPaidMerchandise =
    (isPaidCheckoutMerchandiseLine(cartItem) || hasChargedAmount) && !displayAsCartFreeGift;
  const effectivePreview =
    hideControls ||
    (preview && !isPaidMerchandise) ||
    (displayAsCartFreeGift && !hasChargedAmount && !isSameCatalogueFreeLine);
  const showQuantityControls =
    !effectivePreview &&
    !displayAsCartFreeGift &&
    Boolean(onChangeQuantity) &&
    (isPaidMerchandise || (!isGiftPromotedProduct && (hasCost || isSameCatalogueFreeLine)));
  const imageGiftPromotedFlag = isGiftPromotedProduct && !showAsFreeGift && !isPaidMerchandise;

  return (
    <div className={cn("relative bg-white p-4", !preview ? "md:pr-[3.75rem]" : "")}>
      <div
        className={cn(
          "mb-5 flex flex-col gap-3 border-b md:flex-row md:gap-6",
          !preview ? "md:my-5" : "",
          !hasArrayElements(lensAddOns) && "md:pb-4",
        )}
      >
        <CartImage
          cartItem={cartItem}
          src={
            itemInfo?.thumbnail?.value?.split(",")?.[0] ||
            itemInfo?.clThumbnail?.value?.split(",")?.[0] ||
            ""
          }
          variantId={variantId}
          productUrl={productUrl}
          staticText={staticText}
          preview={effectivePreview}
          onRemove={() => (onDeleteItem ? onDeleteItem(cartItem) : null)}
          isGiftPromotedProduct={imageGiftPromotedFlag}
        />
        <div className="mt-3 flex w-full flex-col">
          <div className="flex justify-between">
            <CartInfo
              title={
                lang === "ar" ? product?.brand?.translation?.name || "" : product?.brand?.name || ""
              }
              itemInfo={itemInfo}
              itemDetails={itemDetails}
              staticText={staticText}
              promotionBadgeName={
                showAsFreeGift || isGiftPromotedProduct ? "" : promotionBadgeName
              }
            />

            <CartPricing
              hasCost={displayHasCost}
              isOutOfStock={isOutOfStock}
              quantity={cartItem?.quantity}
              currency={currency}
              lang={lang}
              discount={cardDiscount}
              // TODO(QA): When Qatar (qa) is enabled, stop hardcoding true — use displayPercentage from
              // NEXT_PUBLIC_PERCENTAGE_SYMBOL_HIDE_COUNTRIES / getPercentHideCountries() (same as Magrabi).
              displayPercentage
              isFlatOff={showCardPromotionalPrice && (isFlatOff || displayIsFlatOff)}
              listPrice={cardListPrice}
              isDiscounted={showCardPromotionalPrice}
              salePrice={effectiveSalePrice}
              freeLabel={staticText?.cart_free_label}
              qtyLabel={staticText?.cart_qty_label}
              preview={effectivePreview}
              showQuantityControls={showQuantityControls}
              onChangeQuantity={(quantity) =>
                onChangeQuantity ? onChangeQuantity(quantity, cartItem) : null
              }
            />
          </div>

          {hasArrayElements(lensAddOns) ? (
            <>
              <div className="mt-[2px] border-b border-tertiary-3" />
              <CartItemFeatures
                lineItem={{ ...cartItem, addOns: lensAddOns }}
                currency={currency}
                language={lang}
                translations={staticText}
                price={selectedLenPrice}
                priceUndiscounted={undiscountedSelectedLenPrice}
              />
            </>
          ) : (
            ""
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2 md:flex-row">
          {isOutOfStock && (
            <div className="flex items-center gap-1 bg-error px-3 py-1">
              <p className="text-[0.75rem] lg:text-[0.813rem]">
                {staticText?.cart_out_of_stock_label || ""}
              </p>
            </div>
          )}
          {!isOutOfStock && itemInfo?.isReturnable?.value !== "1" && (
            <CNCBadge
              badges={[
                {
                  badge: "NotReturnable",
                  label: staticText?.cart_cannot_be_exchanged_message_text || "",
                },
              ]}
            />
          )}
          {!isOutOfStock && enableClickAndCollect && (
            <CNCBadge
              badges={[
                {
                  badge: "ClickNCollect",
                  label: staticText?.shipping_click_and_collect_label || "Click & Collect",
                },
              ]}
            />
          )}
          {showExclusiveBrandCouponMessage &&
            cartItem?.isExcludedFromVoucherDueToExclusiveBrand && (
            <p className="mt-1 text-[0.75rem] text-secondary-1 lg:text-[0.813rem]">
              {staticText?.cart_coupon_not_applicable_exclusive_brand_line_message}
            </p>
          )}
        </div>
        <p className="hidden text-end text-sm font-semibold md:block">
          <span className="mr-1">{staticText?.cart_item_total_label || ""} :</span>
          <span className={cn(!displayHasCost && "text-success")}>
            {displayHasCost ? (
              <RenderPrice price={formattedEffectiveItemTotal ?? 0} currencyCode={currency} lang={lang} />
            ) : (
              staticText?.cart_free_label || "Free"
            )}
          </span>
        </p>
      </div>

      {/* <SelectedLens /> */}
      {(isOutOfStock ||
        enableClickAndCollect ||
        itemInfo?.isReturnable?.value !== "1" ||
        (showExclusiveBrandCouponMessage &&
          cartItem?.isExcludedFromVoucherDueToExclusiveBrand)) && (
        <Divider containerClassName="my-5 flex md:hidden" lineClassName="bg-[#EEEEEE]" />
      )}

      <div className="flex justify-end md:hidden">
        <p className="text-sm font-semibold">
          <span className="mr-1">{staticText?.cart_item_total_label || ""} :</span>
          <span className={cn(!displayHasCost && "text-success")}>
            {displayHasCost ? (
              <RenderPrice price={formattedEffectiveItemTotal ?? 0} currencyCode={currency} lang={lang} />
            ) : (
              staticText?.cart_free_label || "Free"
            )}
          </span>
        </p>
      </div>
    </div>
  );
};

export default CartItemCard;
